import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, LogOut, ArrowRight, Plus, User, Clock, Users, UserPlus, Info, Trash2, Share2, X, Mail, Phone, Bell, Camera, Image, Sparkles, Book, Globe, Building2 } from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import FamilyShareModal from '../components/FamilyShareModal';

export default function HomeScreen() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const photoInputRef = useRef(null);
  
  const [showNewForm, setShowNewForm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [secondPersonFirstName, setSecondPersonFirstName] = useState('');
  const [secondPersonLastName, setSecondPersonLastName] = useState('');
  const [storyType, setStoryType] = useState('individual'); // 'individual' or 'couple'
  const [hasInterviewer, setHasInterviewer] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [lifeStories, setLifeStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteStory, setInviteStory] = useState(null);
  const [inviteMethod, setInviteMethod] = useState('email'); // 'email' or 'sms'
  const [smsConsent, setSmsConsent] = useState(false); // SMS opt-in consent
  const [inviteContact, setInviteContact] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRelationship, setInviteRelationship] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  
  // Photo update for existing story
  const [updatingPhotoStoryId, setUpdatingPhotoStoryId] = useState(null);
  const updatePhotoInputRef = useRef(null);
  
  // Family Portal modal state
  const [showFamilyPortal, setShowFamilyPortal] = useState(false);
  const [familyPortalStory, setFamilyPortalStory] = useState(null);

  // Load user's life stories
  useEffect(() => {
    if (!user?.uid) return;
    
    const q = query(
      collection(db, 'lifeStories'),
      where('createdBy', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const stories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(story => !story.communityId); // Exclude community residents
      setLifeStories(stories);
      setLoading(false);
    }, (err) => {
      console.error('Error loading life stories:', err);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [user?.uid]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim()) {
      setError('Please enter the person\'s first name');
      return;
    }
    
    if (!lastName.trim()) {
      setError('Please enter the person\'s last name');
      return;
    }
    
    if (storyType === 'couple' && (!secondPersonFirstName.trim() || !secondPersonLastName.trim())) {
      setError('Please enter both names for the couple');
      return;
    }
    
    setCreating(true);
    setError(null);
    
    try {
      // Determine speaker count for diarization
      let expectedSpeakers = 1;
      if (storyType === 'couple') expectedSpeakers = 2;
      if (hasInterviewer) expectedSpeakers += 1;
      
      // Full name for display
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      
      // Build the life story document
      const storyData = {
        residentName: fullName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        createdBy: user.uid,
        createdByName: user.displayName || user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'not_started',
        completionPercentage: 0,
        consent: null,
        preferredName: null,
        profilePhotoUrl: null,
        
        // Story configuration
        storyType: storyType, // 'individual' or 'couple'
        hasInterviewer: hasInterviewer,
        expectedSpeakers: expectedSpeakers,
        
        // Enable diarization if more than one speaker
        useSpeakerDiarization: expectedSpeakers > 1,
        
        // Family contributions
        pendingContributions: 0,
        
        // Linking to Quiltt (null until linked)
        linkedCommunityId: null,
        linkedResidentId: null,
        linkedAt: null,
        sharedWithStaff: false
      };
      
      // Add second person for couples
      if (storyType === 'couple') {
        storyData.secondPerson = {
          name: `${secondPersonFirstName.trim()} ${secondPersonLastName.trim()}`,
          firstName: secondPersonFirstName.trim(),
          lastName: secondPersonLastName.trim(),
          preferredName: null
        };
      }
      
      const docRef = await addDoc(collection(db, 'lifeStories'), storyData);
      
      // Upload profile photo if provided
      if (profilePhoto) {
        const photoFileName = `profile_${Date.now()}.jpg`;
        const photoRef = ref(storage, `life-stories/${docRef.id}/photos/${photoFileName}`);
        await uploadBytes(photoRef, profilePhoto);
        const photoUrl = await getDownloadURL(photoRef);
        
        await updateDoc(doc(db, 'lifeStories', docRef.id), {
          profilePhotoUrl: photoUrl
        });
      }
      
      // Reset form
      setFirstName('');
      setLastName('');
      setSecondPersonFirstName('');
      setSecondPersonLastName('');
      setProfilePhoto(null);
      setProfilePhotoPreview(null);
      setStoryType('individual');
      setHasInterviewer(false);
      
      // Navigate to the recording screen
      navigate(`/story/${docRef.id}`);
      
    } catch (err) {
      console.error('Error creating life story:', err);
      setError('Failed to create life story. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setShowNewForm(false);
    setFirstName('');
    setLastName('');
    setSecondPersonFirstName('');
    setSecondPersonLastName('');
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    setStoryType('individual');
    setHasInterviewer(false);
    setError(null);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setProfilePhoto(null);
    if (profilePhotoPreview) {
      URL.revokeObjectURL(profilePhotoPreview);
      setProfilePhotoPreview(null);
    }
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleUpdatePhoto = async (e, storyId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUpdatingPhotoStoryId(storyId);
    
    try {
      const photoFileName = `profile_${Date.now()}.jpg`;
      const photoRef = ref(storage, `life-stories/${storyId}/photos/${photoFileName}`);
      await uploadBytes(photoRef, file);
      const photoUrl = await getDownloadURL(photoRef);
      
      await updateDoc(doc(db, 'lifeStories', storyId), {
        profilePhotoUrl: photoUrl
      });
    } catch (err) {
      console.error('Error updating photo:', err);
      alert('Failed to update photo. Please try again.');
    } finally {
      setUpdatingPhotoStoryId(null);
      if (updatePhotoInputRef.current) {
        updatePhotoInputRef.current.value = '';
      }
    }
  };

  const triggerPhotoUpdate = (e, storyId) => {
    e.stopPropagation();
    setUpdatingPhotoStoryId(storyId);
    updatePhotoInputRef.current?.click();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStoryLabel = (story) => {
    const labels = [];
    if (story.storyType === 'couple') labels.push('Couple');
    if (story.hasInterviewer) labels.push('Interview');
    return labels.join(' • ');
  };

  const handleDelete = async (e, storyId, storyName) => {
    e.stopPropagation(); // Prevent navigating to the story
    
    const confirmed = window.confirm(
      `Are you sure you want to delete "${storyName}"?\n\nThis will permanently delete all recordings and cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      await deleteDoc(doc(db, 'lifeStories', storyId));
      // Note: Audio files in Storage will remain (could add cleanup function later)
    } catch (err) {
      console.error('Error deleting story:', err);
      alert('Failed to delete story. Please try again.');
    }
  };

  const openInviteModal = (e, story) => {
    e.stopPropagation();
    setInviteStory(story);
    setInviteMethod('email');
    setInviteContact('');
    setInviteName('');
    setInviteRelationship('');
    setInviteError(null);
    setInviteSuccess(false);
    setShowInviteModal(true);
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteStory(null);
    setInviteError(null);
    setInviteSuccess(false);
    setSmsConsent(false);
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!inviteName.trim()) {
      setInviteError('Please enter the person\'s name');
      return;
    }
    
    if (!inviteContact.trim()) {
      setInviteError(`Please enter ${inviteMethod === 'email' ? 'an email address' : 'a phone number'}`);
      return;
    }
    
    // Basic validation
    if (inviteMethod === 'email' && !inviteContact.includes('@')) {
      setInviteError('Please enter a valid email address');
      return;
    }
    
    setSendingInvite(true);
    setInviteError(null);
    
    try {
      const functions = getFunctions();
      const sendInvite = httpsCallable(functions, 'sendContributionInvite');
      
      await sendInvite({
        storyId: inviteStory.id,
        residentName: inviteStory.preferredName || inviteStory.residentName,
        contributorName: inviteName.trim(),
        contributorContact: inviteContact.trim(),
        contactMethod: inviteMethod,
        relationship: inviteRelationship.trim() || null
      });
      
      setInviteSuccess(true);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        closeInviteModal();
      }, 2000);
      
    } catch (err) {
      console.error('Error sending invite:', err);
      setInviteError(err.message || 'Failed to send invite. Please try again.');
    } finally {
      setSendingInvite(false);
    }
  };

  // Get pending contribution count for badge
  const getPendingCount = (story) => {
    return story.pendingContributions || 0;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://cdn.prod.website-files.com/6005bb130071e76d42c92ec9/608bf8a812544a0b1a4c7b3e_quiltt-logo-horiz.png" 
              alt="Quiltt" 
              className="h-8"
            />
            <span className="font-medium text-gray-400 text-sm">Life Story Capture</span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 hidden sm:block">{user?.displayName || user?.email}</span>
            
            {/* Super admin controls */}
            {user?.isSuperAdmin && (
              <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
                <button
                  onClick={() => navigate('/communities')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-quiltt-purple hover:bg-quiltt-50 rounded-lg transition-colors"
                  title="View all communities"
                >
                  <Building2 className="w-4 h-4" />
                  <span className="hidden md:inline">Communities</span>
                </button>
                <button
                  onClick={() => navigate('/community/setup')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-quiltt-purple hover:bg-purple-700 rounded-lg transition-colors"
                  title="Create new community"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden md:inline">New Community</span>
                </button>
              </div>
            )}
            
            <button
              onClick={handleSignOut}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        
        {/* My Community section — shown if user is community staff */}
        {user?.communityId && (
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">My Community</h2>
            <button
              onClick={() => navigate(`/community/${user.communityId}`)}
              className="w-full bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-quiltt-purple/30 transition-all p-5 text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-quiltt-50 flex items-center justify-center flex-shrink-0 group-hover:bg-quiltt-purple/10 transition-colors">
                  <Building2 className="w-6 h-6 text-quiltt-purple" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{user.communityName || 'Community Dashboard'}</h3>
                  <p className="text-sm text-gray-500">
                    {user.communityRole === 'admin' ? 'Administrator' : 'Staff Member'}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-quiltt-purple transition-colors" />
              </div>
            </button>
          </div>
        )}

        {/* Personal stories section */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-quiltt-navy">
              {user?.communityId ? 'My Personal Stories' : 'Life Stories'}
            </h2>
            <p className="text-gray-600 mt-1">
              {user?.communityId 
                ? 'Stories you\'ve created for family or friends outside of your community.'
                : 'Capture and preserve memories that matter.'
              }
            </p>
          </div>
          
          {!showNewForm && (
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">New Life Story</span>
            </button>
          )}
        </div>
        
        {/* New life story form */}
        {showNewForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Start a New Life Story</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            
            <form onSubmit={handleCreate}>
              {/* Story Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Who is this story for?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setStoryType('individual')}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center text-center transition-all ${
                      storyType === 'individual' 
                        ? 'border-quiltt-navy bg-quiltt-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className={`w-6 h-6 mb-2 ${storyType === 'individual' ? 'text-quiltt-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${storyType === 'individual' ? 'text-quiltt-700' : 'text-gray-600'}`}>
                      Individual
                    </span>
                    <span className="text-xs text-gray-500 mt-1">One person's story</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setStoryType('couple')}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center text-center transition-all ${
                      storyType === 'couple' 
                        ? 'border-quiltt-navy bg-quiltt-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className={`w-6 h-6 mb-2 ${storyType === 'couple' ? 'text-quiltt-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${storyType === 'couple' ? 'text-quiltt-700' : 'text-gray-600'}`}>
                      Couple
                    </span>
                    <span className="text-xs text-gray-500 mt-1">Two people together</span>
                  </button>
                </div>
              </div>
              
              {/* Name inputs */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {storyType === 'couple' ? "First Person's Name" : "Person's Name"}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    autoFocus
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
              
              {/* Second person for couples */}
              {storyType === 'couple' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Second Person's Name
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={secondPersonFirstName}
                      onChange={(e) => setSecondPersonFirstName(e.target.value)}
                      placeholder="First name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                    />
                    <input
                      type="text"
                      value={secondPersonLastName}
                      onChange={(e) => setSecondPersonLastName(e.target.value)}
                      placeholder="Last name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              )}
              
              {/* Profile Photo */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo (optional)
                </label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                {profilePhotoPreview ? (
                  <div className="flex items-center gap-4">
                    <img
                      src={profilePhotoPreview}
                      alt="Preview"
                      className="w-20 h-20 object-cover rounded-xl"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        className="px-3 py-2 text-sm text-quiltt-600 hover:bg-gray-50 rounded-lg"
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-quiltt-400 transition-colors flex items-center justify-center gap-2 text-gray-500"
                  >
                    <Camera className="w-5 h-5" />
                    Add a photo
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  This photo will be shown to family members when they contribute.
                </p>
              </div>
              
              {/* Interviewer toggle */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasInterviewer}
                    onChange={(e) => setHasInterviewer(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-quiltt-navy focus:ring-quiltt-500"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-gray-600" />
                      <span className="font-medium text-gray-900">Someone will conduct the interview</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {storyType === 'couple' 
                        ? "A family member or friend will ask questions to both people and record the conversation."
                        : "A family member or friend will ask questions and record the conversation."
                      }
                      {' '}We'll automatically identify each person's voice in the transcript.
                    </p>
                  </div>
                </label>
              </div>
              
              {/* Info box */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-3">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Flexible recording sessions</p>
                  <p>
                    Different people can help record at different times. For example, a granddaughter 
                    could conduct one session, then a grandson could do another session later. 
                    Each recording will identify who's speaking, and all responses are saved together.
                  </p>
                </div>
              </div>
              
              <p className="mb-4 text-sm text-gray-500">
                You can link this to their Quiltt profile later.
              </p>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-quiltt-navy/50 text-white font-medium py-2.5 px-5 rounded-lg transition-colors"
                >
                  {creating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Start Recording
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Life stories list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-quiltt-navy border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        ) : lifeStories.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No life stories yet</h3>
            <p className="text-gray-500 mb-6">Start capturing a resident's story today.</p>
            {!showNewForm && (
              <button
                onClick={() => setShowNewForm(true)}
                className="inline-flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Life Story
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {lifeStories.map((story) => (
              <div
                key={story.id}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:border-quiltt-300 hover:shadow-sm transition-all"
              >
                {/* Profile photo with edit button */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => navigate(`/story/${story.id}`)}
                    className="block"
                  >
                    {story.profilePhotoUrl ? (
                      <img
                        src={story.profilePhotoUrl}
                        alt={story.residentName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-quiltt-100 rounded-full flex items-center justify-center">
                        {story.storyType === 'couple' ? (
                          <Users className="w-6 h-6 text-quiltt-600" />
                        ) : (
                          <User className="w-6 h-6 text-quiltt-600" />
                        )}
                      </div>
                    )}
                  </button>
                  {/* Photo edit button */}
                  <button
                    onClick={(e) => triggerPhotoUpdate(e, story.id)}
                    disabled={updatingPhotoStoryId === story.id}
                    className="absolute -bottom-1 -right-1 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 shadow-sm"
                    title={story.profilePhotoUrl ? "Change photo" : "Add photo"}
                  >
                    {updatingPhotoStoryId === story.id ? (
                      <div className="w-3 h-3 border-2 border-quiltt-navy border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-3 h-3 text-gray-500" />
                    )}
                  </button>
                </div>
                
                <button
                  onClick={() => navigate(`/story/${story.id}`)}
                  className="flex items-center gap-4 flex-1 min-w-0 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">
                      {story.storyType === 'couple' 
                        ? `${story.preferredName || story.residentName} & ${story.secondPerson?.preferredName || story.secondPerson?.name}`
                        : story.preferredName || story.residentName
                      }
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(story.updatedAt || story.createdAt)}
                      </span>
                      <span>•</span>
                      <span>{story.completionPercentage || 0}% complete</span>
                      {getStoryLabel(story) && (
                        <>
                          <span>•</span>
                          <span className="text-quiltt-600">{getStoryLabel(story)}</span>
                        </>
                      )}
                      {story.linkedCommunityId && (
                        <>
                          <span>•</span>
                          <span className="text-green-600">Linked to Quiltt</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0 hidden sm:block">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-gray-700">
                        {story.completionPercentage || 0}%
                      </span>
                    </div>
                  </div>
                  
                  <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </button>
                
                {/* Action buttons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Contributions button - always visible */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/story/${story.id}/contributions`);
                    }}
                    className={`relative p-2 rounded-lg transition-colors ${
                      getPendingCount(story) > 0 
                        ? 'text-quiltt-navy hover:text-quiltt-600 hover:bg-gray-50' 
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                    }`}
                    title={getPendingCount(story) > 0 
                      ? `${getPendingCount(story)} pending contribution${getPendingCount(story) > 1 ? 's' : ''}` 
                      : 'View contributions'}
                  >
                    <Bell className="w-5 h-5" />
                    {getPendingCount(story) > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {getPendingCount(story)}
                      </span>
                    )}
                  </button>
                  
                  {/* Invite button */}
                  <button
                    onClick={(e) => openInviteModal(e, story)}
                    className="p-2 text-gray-400 hover:text-quiltt-navy hover:bg-gray-50 rounded-lg transition-colors"
                    title="Invite family to contribute"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                  
                  {/* Family Portal button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFamilyPortalStory(story);
                      setShowFamilyPortal(true);
                    }}
                    className={`p-2 rounded-lg transition-colors ${
                      story.familyPortal 
                        ? 'text-green-500 hover:text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                    }`}
                    title={story.familyPortal ? 'Manage Family Portal' : 'Create Family Portal'}
                  >
                    <Globe className="w-5 h-5" />
                  </button>
                  
                  {/* AI Summaries button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/story/${story.id}/summaries`);
                    }}
                    className={`p-2 rounded-lg transition-colors ${
                      story.aiSummaries 
                        ? 'text-purple-500 hover:text-purple-600 hover:bg-purple-50' 
                        : 'text-gray-400 hover:text-purple-500 hover:bg-purple-50'
                    }`}
                    title={story.aiSummaries ? 'View AI summaries' : 'Generate AI summaries'}
                  >
                    <Sparkles className="w-5 h-5" />
                  </button>
                  
                  {/* Print Shop button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/story/${story.id}/print`);
                    }}
                    className="p-2 text-gray-400 hover:text-quiltt-purple hover:bg-purple-50 rounded-lg transition-colors"
                    title="Order printed book"
                  >
                    <Book className="w-5 h-5" />
                  </button>
                  
                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, story.id, story.preferredName || story.residentName)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete story"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      
      {/* Invite Modal */}
      {showInviteModal && inviteStory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Invite Family to Contribute
                </h2>
                <button
                  onClick={closeInviteModal}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {inviteSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Share2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Invite Sent!</h3>
                  <p className="text-gray-600">
                    {inviteName} will receive an invitation to contribute to {inviteStory.preferredName || inviteStory.residentName}'s story.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSendInvite}>
                  <p className="text-gray-600 mb-6">
                    Invite someone to share memories, answer questions, or add photos to <strong>{inviteStory.preferredName || inviteStory.residentName}'s</strong> life story.
                  </p>
                  
                  {inviteError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {inviteError}
                    </div>
                  )}
                  
                  {/* Contributor name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Their Name *
                    </label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="e.g., Sarah"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                    />
                  </div>
                  
                  {/* Relationship */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship (optional)
                    </label>
                    <input
                      type="text"
                      value={inviteRelationship}
                      onChange={(e) => setInviteRelationship(e.target.value)}
                      placeholder="e.g., granddaughter, niece, friend"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                    />
                  </div>
                  
                  {/* Contact method tabs */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Send invite via
                    </label>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          setInviteMethod('email');
                          setInviteContact('');
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 transition-colors ${
                          inviteMethod === 'email'
                            ? 'border-quiltt-navy bg-quiltt-50 text-quiltt-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <Mail className="w-4 h-4" />
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setInviteMethod('sms');
                          setInviteContact('');
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 transition-colors ${
                          inviteMethod === 'sms'
                            ? 'border-quiltt-navy bg-quiltt-50 text-quiltt-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <Phone className="w-4 h-4" />
                        Text
                      </button>
                    </div>
                    
                    <input
                      type={inviteMethod === 'email' ? 'email' : 'tel'}
                      value={inviteContact}
                      onChange={(e) => setInviteContact(e.target.value)}
                      placeholder={inviteMethod === 'email' ? 'email@example.com' : '(555) 123-4567'}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                    />
                  </div>
                  
                  {/* SMS Consent Checkbox */}
                  {inviteMethod === 'sms' && (
                    <div className="mb-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={smsConsent}
                          onChange={(e) => setSmsConsent(e.target.checked)}
                          className="mt-1 w-4 h-4 text-quiltt-purple border-gray-300 rounded focus:ring-quiltt-purple"
                        />
                        <span className="text-sm text-gray-600">
                          I confirm that <strong>{inviteName || 'this person'}</strong> has given verbal or written consent to receive a text message invitation to contribute to this life story. 
                          <span className="text-gray-500 block mt-1">Standard message rates may apply. They can opt out by replying STOP.</span>
                        </span>
                      </label>
                    </div>
                  )}
                  
                  {/* Info */}
                  <div className="mb-6 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    {inviteMethod === 'email' 
                      ? "They'll receive a unique link to contribute. No account needed to add memories."
                      : "They'll receive a one-time text with a link to contribute. No account needed."
                    }
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={closeInviteModal}
                      className="flex-1 px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={sendingInvite || (inviteMethod === 'sms' && !smsConsent)}
                      className="flex-1 flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                    >
                      {sendingInvite ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Share2 className="w-4 h-4" />
                          Send Invite
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Family Portal Modal */}
      {showFamilyPortal && familyPortalStory && (
        <FamilyShareModal
          isOpen={showFamilyPortal}
          onClose={() => {
            setShowFamilyPortal(false);
            setFamilyPortalStory(null);
          }}
          storyId={familyPortalStory.id}
          storyName={familyPortalStory.preferredName || familyPortalStory.residentName || familyPortalStory.subjectName || ''}
        />
      )}
      
      {/* Hidden file input for updating existing story photos */}
      <input
        ref={updatePhotoInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleUpdatePhoto(e, updatingPhotoStoryId)}
        className="hidden"
      />
    </div>
  );
}
